import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from "react-redux";
import {getDate} from "../../redux/slice/dateSlice";
import {
    getRequestBulletin,
    getRequestNotice,
    getServiceByStatus,
    getServiceCodeStatic,
    getServiceDetail,
    getTimelyThroughput,
} from "../../api/static/StaticRequestService";
import ChartLoadingOverlay from "../../components/Utils/ChartLoadingOverlay";
import {TotalNotice} from "../chart/TotalNotice";
import {TotalBulletin} from "../chart/TotalBulletin";
import {
    ServiceTaskProcessStatic,
    ServiceStatusByStageStatic,
} from "../chart/ManagedTaskStatic";
import {
    ServiceProcessStatusStatic,
    ServiceRequestTabs,
    ServiceTimelyRateCard,
    ServiceTrendStatusStatic,
} from "../chart/ServiceDashboardWidgets";

const extractResponseData = (result) => {
    if (result?.status !== "fulfilled") return null;
    const response = result.value;
    if (response?.status === 200) return response.data;
    return null;
};

const toTabMap = (raw) => {
    if (!raw) return {};
    if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) return raw.data;
    if (typeof raw === "object" && !Array.isArray(raw)) return raw;
    return {};
};

const toTimelyData = (raw) => {
    if (!raw) return null;
    const payload = raw.left != null || raw.right != null ? raw : raw.data;
    if (!payload) return null;
    return {
        title: raw.title ?? "종합처리율",
        left: {
            ...payload.left,
            title: payload.left?.title ?? "금월 적기 처리율",
        },
        right: payload.right,
    };
};

function DashBoard_Service() {
    const taskProcessQuery = useSelector((state) => getDate(state, "업무별 처리현황"));
    const statusQuery = useSelector((state) => getDate(state, "진행단계별 현황"));

    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState({
        taskProcess: null,
        serviceDetail: null,
        statusByStage: null,
        timelyThroughput: null,
        notice: null,
        bulletin: null,
    });

    const fetchDashboard = useCallback(async () => {
        setLoading(true);

        const results = await Promise.allSettled([
            getServiceCodeStatic(taskProcessQuery),
            getServiceDetail(),
            getServiceByStatus(statusQuery),
            getTimelyThroughput(),
            getRequestNotice("request"),
            getRequestBulletin("request"),
        ]);

        setDashboardData({
            taskProcess: extractResponseData(results[0]),
            serviceDetail: extractResponseData(results[1]),
            statusByStage: extractResponseData(results[2]),
            timelyThroughput: extractResponseData(results[3]),
            notice: extractResponseData(results[4]),
            bulletin: extractResponseData(results[5]),
        });
        setLoading(false);
    }, [taskProcessQuery, statusQuery]);

    const reloadTaskProcess = useCallback(async () => {
        try {
            const response = await getServiceCodeStatic(taskProcessQuery);
            if (response?.status === 200) {
                setDashboardData((prev) => ({...prev, taskProcess: response.data}));
            }
        } catch {
            // ignore
        }
    }, [taskProcessQuery]);

    const reloadStatus = useCallback(async () => {
        try {
            const response = await getServiceByStatus(statusQuery);
            if (response?.status === 200) {
                setDashboardData((prev) => ({...prev, statusByStage: response.data}));
            }
        } catch {
            // ignore
        }
    }, [statusQuery]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    useEffect(() => {
        if (!loading) reloadTaskProcess();
    }, [taskProcessQuery, reloadTaskProcess, loading]);

    useEffect(() => {
        if (!loading) reloadStatus();
    }, [statusQuery, reloadStatus, loading]);

    const serviceTabData = useMemo(
        () => toTabMap(dashboardData.serviceDetail),
        [dashboardData.serviceDetail]
    );
    const timelyData = useMemo(
        () => toTimelyData(dashboardData.timelyThroughput),
        [dashboardData.timelyThroughput]
    );

    const noticeItems = Array.isArray(dashboardData.notice)
        ? dashboardData.notice
        : Array.isArray(dashboardData.notice?.data)
            ? dashboardData.notice.data
            : [];

    return (
        <div className="common-container flex min-h-0 flex-col">
            <div
                className="flex flex-col gap-4 md:grid md:min-h-0 md:flex-1 md:[grid-template-columns:repeat(10,minmax(0,1fr))] md:[grid-template-rows:minmax(0,2fr)_minmax(0,2fr)_minmax(0,1.5fr)]"
            >
                {/* 1행 — 1.5 : 2 : 1.5 */}
                <div className="flex min-h-[320px] flex-col overflow-hidden border rounded-si p-[16px] [&>*]:min-h-0 [&>*]:flex-1 md:col-span-3 md:h-full md:min-h-0">
                    <ServiceTaskProcessStatic
                        data={dashboardData.taskProcess}
                        loading={loading && !dashboardData.taskProcess}
                    />
                </div>
                <div className="relative flex min-h-[320px] flex-col overflow-hidden border rounded-si p-[16px] [&>*]:min-h-0 [&>*]:flex-1 md:col-span-4 md:h-full md:min-h-0">
                    {loading && <ChartLoadingOverlay />}
                    <ServiceRequestTabs tabMap={serviceTabData} />
                </div>
                <div className="flex min-h-[320px] flex-col overflow-hidden border rounded-si p-[16px] [&>*]:min-h-0 [&>*]:flex-1 md:col-span-3 md:h-full md:min-h-0">
                    <ServiceStatusByStageStatic
                        data={dashboardData.statusByStage}
                        loading={loading && !dashboardData.statusByStage}
                    />
                </div>

                {/* 2행 — 종합처리율 | 추이현황 | 처리현황 */}
                <div className="flex min-h-[320px] flex-col overflow-hidden border rounded-si p-[16px] [&>*]:min-h-0 [&>*]:flex-1 md:col-span-2 md:h-full md:min-h-0">
                    <ServiceTimelyRateCard data={timelyData} loading={loading && !timelyData} />
                </div>
                <div className="flex min-h-[320px] flex-col overflow-hidden border rounded-si p-[16px] [&>*]:min-h-0 [&>*]:flex-1 md:col-span-4 md:h-full md:min-h-0">
                    <ServiceTrendStatusStatic />
                </div>
                <div className="flex min-h-[320px] flex-col overflow-hidden border rounded-si p-[16px] [&>*]:min-h-0 [&>*]:flex-1 md:col-span-4 md:h-full md:min-h-0">
                    <ServiceProcessStatusStatic />
                </div>

                {/* 3행 — 공지 + 자료실 */}
                <div
                    className="flex flex-col gap-4 md:col-span-10 md:grid md:min-h-0 md:grid-cols-[minmax(0,7fr)_minmax(0,3fr)]"
                >
                    <div className="flex min-h-[300px] flex-col overflow-hidden border rounded-si p-[16px] md:h-full md:min-h-0">
                        <TotalNotice data={noticeItems} loading={loading} />
                    </div>
                    <div className="flex min-h-[300px] flex-col overflow-hidden border rounded-si p-[16px] md:h-full md:min-h-0">
                        <TotalBulletin data={dashboardData.bulletin} loading={loading} />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DashBoard_Service;
